import { useState, useCallback } from 'react';
import { apiService, CustomFieldValue } from '../../../services/api';
import type { UndoAction } from '../types';

// Re-export type for convenience
export type { UndoAction } from '../types';

type UndoState = {
  actions: UndoAction[];
  maxHistorySize: number;
};

interface Founder {
  id: string;
  customFieldValues?: CustomFieldValue[];
  [key: string]: any;
}

interface UseUndoRedoProps {
  handleFounderUpdate: (founderId: string, updates: any) => void;
  filteredFounders: Founder[];
}

const FIELD_MAP: Record<string, string> = {
  email: 'email',
  company: 'company',
  position: 'roleTitle',
  linkedinUrl: 'profileUrl',
  industry: 'industry',
  location: 'location',
  stage: 'stage',
  status: 'status',
  tag: 'tag',
};

export function useUndoRedo({ handleFounderUpdate, filteredFounders }: UseUndoRedoProps) {
  const [undoState, setUndoState] = useState<UndoState>({
    actions: [],
    maxHistorySize: 50,
  });

  const addUndoAction = useCallback((action: UndoAction) => {
    setUndoState((prev) => ({
      ...prev,
      actions: [...prev.actions.slice(-prev.maxHistorySize + 1), action],
    }));
  }, []);

  const performUndo = useCallback(async () => {
    if (undoState.actions.length === 0) return;

    const lastAction = undoState.actions[undoState.actions.length - 1];
    const founder = filteredFounders.find(f => f.id === lastAction.founderId);

    try {
      if (lastAction.isCustomField) {
        // Optimistic update for custom fields
        if (founder) {
          const currentValues = founder.customFieldValues || [];
          const existingEntry = currentValues.find(cfv => cfv.fieldId === lastAction.colKey);
          let updatedValues: CustomFieldValue[];
          if (existingEntry) {
            updatedValues = currentValues.map(cfv =>
              cfv.fieldId === lastAction.colKey ? { ...cfv, value: lastAction.oldValue } : cfv
            );
          } else {
            // Create a minimal CustomFieldValue for display purposes
            // The full object will be populated when the API response comes back
            updatedValues = [...currentValues, {
              id: `temp-${Date.now()}`,
              fieldId: lastAction.colKey,
              value: lastAction.oldValue,
              founderId: lastAction.founderId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              field: {} as any, // Will be populated on next fetch
            }];
          }
          handleFounderUpdate(lastAction.founderId, { customFieldValues: updatedValues });
        }

        // API call
        await apiService.setCustomFieldValue(
          lastAction.colKey,
          lastAction.founderId,
          lastAction.oldValue
        );
      } else {
        // Built-in field: optimistic update
        const displayField = FIELD_MAP[lastAction.colKey] || lastAction.colKey;
        handleFounderUpdate(lastAction.founderId, {
          [displayField]: lastAction.oldValue,
        });

        // API call
        const apiField = FIELD_MAP[lastAction.colKey];
        if (apiField) {
          const payload: Record<string, string> = {};
          payload[apiField] = lastAction.oldValue;
          await apiService.updateFounder(lastAction.founderId, payload);
        }
      }

      // Remove the action from undo history
      setUndoState((prev) => ({
        ...prev,
        actions: prev.actions.slice(0, -1),
      }));
    } catch (error) {
      console.error('Error performing undo:', error);
      // Could add rollback here if needed
    }
  }, [undoState.actions, handleFounderUpdate, filteredFounders]);

  const canUndo = undoState.actions.length > 0;

  return {
    addUndoAction,
    performUndo,
    canUndo,
    undoHistoryLength: undoState.actions.length,
  };
}
